/**
 * <collection-grid-columns>
 *
 * コレクション一覧 (uniform grid) の列数トグル。
 * data-grid で指定したグリッド要素の data-columns 属性を 2 / 3 で切り替える。
 * 選択は localStorage に保存し、次回訪問・フィルター後のリロードでも復元する。
 *
 * Markup:
 *   <collection-grid-columns data-grid="product-grid">
 *     <button data-cols="2">…</button>
 *     <button data-cols="3">…</button>
 *   </collection-grid-columns>
 */
if (!customElements.get('collection-grid-columns')) {
  customElements.define(
    'collection-grid-columns',
    class CollectionGridColumns extends HTMLElement {
      connectedCallback() {
        const gridId = this.getAttribute('data-grid') || 'product-grid';
        this.grid = document.getElementById(gridId);
        this.buttons = Array.from(this.querySelectorAll('[data-cols]'));

        const stored = this.getStored();
        const fallback = (this.grid && this.grid.getAttribute('data-columns')) || '2';
        this.apply(stored || fallback, false);

        this.buttons.forEach((btn) => {
          btn.addEventListener('click', () => this.apply(btn.dataset.cols, true));
        });
      }

      getStored() {
        try {
          return localStorage.getItem('pvz-grid-cols');
        } catch (e) {
          return null;
        }
      }

      apply(cols, persist) {
        if (cols !== '2' && cols !== '3') cols = '2';

        if (this.grid) this.grid.setAttribute('data-columns', cols);

        this.buttons.forEach((btn) => {
          const active = btn.dataset.cols === cols;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        if (persist) {
          try {
            localStorage.setItem('pvz-grid-cols', cols);
          } catch (e) {
            /* localStorage 不可の環境では保存しない */
          }
        }
      }
    }
  );
}

/**
 * <collection-grid-info>
 *
 * コレクション一覧 (uniform grid) の商品情報 (商品名/価格) 表示・非表示トグル。
 * data-grid で指定したグリッドに pvz-ugrid--image-only クラスを付け外しする。
 * 状態は localStorage ('pvz-grid-info' = 'show' | 'hide') に保存し、リロード後も復元する。
 * 初期状態は localStorage 優先、無ければ現在のクラス (= セクション設定 uniform_hide_info) に従う。
 *
 * Markup:
 *   <collection-grid-info data-grid="product-grid">
 *     <button data-info-toggle>…</button>
 *   </collection-grid-info>
 */
if (!customElements.get('collection-grid-info')) {
  customElements.define(
    'collection-grid-info',
    class CollectionGridInfo extends HTMLElement {
      connectedCallback() {
        const gridId = this.getAttribute('data-grid') || 'product-grid';
        this.grid = document.getElementById(gridId);
        this.button = this.querySelector('[data-info-toggle]');

        const stored = this.getStored();
        const initialHidden = stored
          ? stored === 'hide'
          : Boolean(this.grid && this.grid.classList.contains('pvz-ugrid--image-only'));
        this.apply(initialHidden, false);

        if (this.button) {
          this.button.addEventListener('click', () => this.apply(!this.infoHidden, true));
        }
      }

      getStored() {
        try {
          return localStorage.getItem('pvz-grid-info');
        } catch (e) {
          return null;
        }
      }

      // infoHidden: true = 情報を隠す (image-only)
      apply(infoHidden, persist) {
        this.infoHidden = infoHidden;

        if (this.grid) this.grid.classList.toggle('pvz-ugrid--image-only', infoHidden);

        if (this.button) {
          // is-active = 情報を表示している状態
          this.button.classList.toggle('is-active', !infoHidden);
          this.button.setAttribute('aria-pressed', infoHidden ? 'false' : 'true');
        }

        if (persist) {
          try {
            localStorage.setItem('pvz-grid-info', infoHidden ? 'hide' : 'show');
          } catch (e) {
            /* localStorage 不可の環境では保存しない */
          }
        }
      }
    }
  );
}
